import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { Business_Profile_Enum, Role_Assignment_Administrative_Area, Role_Assignment_Brand, Staff_Role_Enum, User_Product_Family, User_Role_Enum } from '@kheops/graphql';
import * as Sentry from '@sentry/react';
import { currentBuyingCompanyIdAtom } from '../basket/state/state';
import { AUTH_TOKEN, AuthToken } from '../common/models/auth.model';
import { CompanyAddress, CompanyRoleAssignment, CompanyServices, ContactInformation, Context, UserDeliveryZone } from '../common/models/user.models';
import { UserRoleQuery, useUserRoleQuery } from '../queries/__generated__/userRole.generated';
import { RoutePaths } from '../routes/AppRoutes';
import { availableContextsAtom, currentContextAtom, userAtom } from '../state';
import { impersonationTokenAtom } from '../common/state/state';
import useAuth from './useAuth/useAuth';

type CompanyContext = Required<UserRoleQuery>['company'][number];
type ProductFamily = Pick<User_Product_Family, 'global_setting' | 'product_family' | 'product_sub_family'>;
type RoleAssignmentWithAdminFlag = NonNullable<UserRoleQuery['user_by_pk']>['role_assignments'][0] & {
  isCompanyAdmin: boolean;
};

export type Brands = Pick<Role_Assignment_Brand, 'brand' | 'has_all_brands'>[];
export type AdministrativeArea = Pick<Role_Assignment_Administrative_Area, 'country_code' | 'area' | 'has_all_areas'>;

export default function useCurrentUser(): void {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryContextId = searchParams.get('contextId');
  const localStorageContextId = localStorage.getItem('contextId');
  const impersonationToken = useAtomValue(impersonationTokenAtom);

  const { isAuthenticated, logout } = useAuth();
  const setUser = useSetAtom(userAtom);
  const setCurrentContext = useSetAtom(currentContextAtom);
  const setAvailableContexts = useSetAtom(availableContextsAtom);
  const setCurrentBuyingCompanyId = useSetAtom(currentBuyingCompanyIdAtom);

  const transformUserRoleIntoContext = useCallback((company: CompanyContext, roleAssignmentId: string, roleName: User_Role_Enum, isCompanyAdmin: boolean, productFamilies?: ProductFamily[], deliveryZones?: UserDeliveryZone[], userBrands?: Brands, userAdministrativeAreas?: AdministrativeArea[]): Context => {
    const { tradeName, legalName, addresses, brand, id, business_profile, photos, contactEmail, contactPhoneNumber, description, delivery_description, delivery_instructions, delivery_email, delivery_phone_number, delivery_hours, delivery_address, invoicing_instructions, invoicing_email, invoicing_phone_number, invoicing_address, supplier_commission_rate, invoice_prefix } = company!;
    let sortedDeliveryZones: UserDeliveryZone[] | undefined;

    if (deliveryZones) {
      const allAreasIndex = deliveryZones.findIndex(
        (zone) => zone.administrative_areas!.some((administrativeArea) => administrativeArea.area === 'ALL_AREAS'),
      );

      if (allAreasIndex !== -1) {
        sortedDeliveryZones = [...deliveryZones];
        sortedDeliveryZones.splice(allAreasIndex, 1);
        sortedDeliveryZones.push(deliveryZones[allAreasIndex]);
      }
    }

    const userRolesMap = new Map<string, CompanyRoleAssignment>();

    company.role_assignments.forEach((companyRoleAssignment) => {
      const existingRole = userRolesMap.get(companyRoleAssignment.user.id);
      if (existingRole) {
        userRolesMap.set(companyRoleAssignment.user.id, {
          ...companyRoleAssignment,
          is_delivery_contact: existingRole.is_delivery_contact || companyRoleAssignment.is_delivery_contact,
          is_invoicing_contact: existingRole.is_invoicing_contact || companyRoleAssignment.is_invoicing_contact,
        });
      } else {
        userRolesMap.set(companyRoleAssignment.user.id, companyRoleAssignment);
      }
    });

    return {
      realm: business_profile,
      roleAssignmentId,
      roleName,
      isCompanyAdmin,
      companyId: id,
      companyName: tradeName,
      companyLegalName: legalName,
      companyBrand: brand,
      companyInvoicePrefix: invoice_prefix,
      companyAddress: {
        administrativeAreaLevel2: company.addresses[0].administrativeAreaLevel2,
        formattedAddress: company.addresses[0].formattedAddress,
        latitude: company.addresses[0].latitude,
        longitude: company.addresses[0].longitude,
        locality: company.addresses[0].locality,
        postalTown: company.addresses[0].postalTown,
        postalCode: company.addresses[0].postalCode,
        countryCode: company.addresses[0].countryCode,
      },
      companyServices: {
        provide_marketing: company.provide_marketing,
        marketing_supports: company.marketing_supports,
        marketing_details: company.marketing_details,
        provide_animation: company.provide_animation,
        animation_details: company.animation_details,
        provide_electrical_appliance: company.provide_electrical_appliance,
        electrical_appliance_details: company.electrical_appliance_details,
        provide_merchandising: company.provide_merchandising,
        merchandising_details: company.merchandising_details,
        provide_return_policy: company.provide_return_policy,
        return_policy_details: company.return_policy_details,
        return_policy: company.return_policy,
      },
      companyRoleAssignments: [...userRolesMap.values()],
      companyGeoloc: {
        lat: addresses[0].latitude,
        lng: addresses[0].longitude,
      },
      companyPhotos: photos,
      productFamilies,
      deliveryZones: sortedDeliveryZones || deliveryZones,
      contactEmail,
      contactPhoneNumber,
      description,
      deliveryDescription: delivery_description,
      deliveryInstructions: delivery_instructions,
      deliveryPhoneNumber: delivery_phone_number,
      deliveryEmail: delivery_email,
      deliveryHours: delivery_hours,
      deliveryAddress: delivery_address,
      invoicingInstructions: invoicing_instructions,
      invoicingPhoneNumber: invoicing_phone_number,
      invoicingEmail: invoicing_email,
      invoicingAddress: invoicing_address,
      commissionRate: supplier_commission_rate as number | undefined,
      userBrands,
      userAdministrativeAreas,
    };
  }, []);

  const getAvailableContexts = useCallback((data: UserRoleQuery): Context[] => {
    if (!data.user_by_pk) {
      return [{
        realm: Business_Profile_Enum.Buyer,
        isCompanyAdmin: false,
        companyId: '',
        companyName: '',
        companyLegalName: '',
        companyInvoicePrefix: '',
        roleAssignmentId: '',
        roleName: User_Role_Enum.BuyerManager,
        companyRoleAssignments: [],
        companyAddress: {} as CompanyAddress,
        companyServices: {} as CompanyServices,
      }];
    }

    const rolesWithoutCompanyDuplicatesMap = data.user_by_pk.role_assignments.reduce((acc, roleAssignment) => {
      const companyId = roleAssignment.company!.id;
      const existingRole = acc.get(companyId);

      if (roleAssignment.role_name === User_Role_Enum.CompanyAdmin) {
        if (existingRole) {
          existingRole.isCompanyAdmin = true;
        } else {
          acc.set(companyId, { ...roleAssignment, isCompanyAdmin: true });
        }
      } else if (!existingRole || existingRole.role_name === User_Role_Enum.CompanyAdmin) {
        acc.set(companyId, { ...roleAssignment, isCompanyAdmin: existingRole?.role_name === User_Role_Enum.CompanyAdmin });
      }

      return acc;
    }, new Map<string, RoleAssignmentWithAdminFlag>());

    const rolesWithoutCompanyDuplicates = [...rolesWithoutCompanyDuplicatesMap.values()];

    const contextList = rolesWithoutCompanyDuplicates.map((roleData) => {
      return transformUserRoleIntoContext(roleData.company!, roleData.id, roleData.role_name!, roleData.isCompanyAdmin, roleData.product_families, roleData.company!.delivery_zones, roleData.brands, roleData.administrative_areas);
    });
    const userIsAdmin = data.user_by_pk.staff_user?.role === Staff_Role_Enum.Admin;

    if (data.company?.[0] && userIsAdmin && !contextList.some((company) => company.companyId === data.company![0].id)) {
      contextList.push(transformUserRoleIntoContext(data.company[0], '', User_Role_Enum.CompanyAdmin, true, undefined, data.company[0].delivery_zones));
    }

    return contextList;
  }, []);

  const verifyContextId = useCallback((contextId: string, userPossibleContext: Context[]): Context | undefined => {
    return userPossibleContext.find((context) => (context.companyId === contextId));
  }, []);

  const getContext = useCallback((userPossibleContext: Context[]): Context => {
    if (queryContextId && !verifyContextId(queryContextId, userPossibleContext)) {
      navigate(RoutePaths.ROOT);
    }

    const context = verifyContextId(queryContextId!, userPossibleContext) || verifyContextId(localStorageContextId!, userPossibleContext) || userPossibleContext[0];
    localStorage.setItem('contextId', context.companyId!);

    return context;
  }, [queryContextId, localStorageContextId]);

  const { userId, sendbirdAccessToken } = useMemo(() => {
    if (!isAuthenticated) {
      return {};
    }
    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token && !impersonationToken) {
      return {};
    }
    const accessToken: AuthToken = impersonationToken || JSON.parse(token!);
    const jwtContent = jwtDecode<{ sub: string }>(accessToken.access_token);

    return {
      userId: jwtContent.sub,
      sendbirdAccessToken: accessToken.sendbird_access_token,
    };
  }, [isAuthenticated, impersonationToken]);

  const companyId = queryContextId || localStorageContextId;
  const { data } = useUserRoleQuery({
    variables: { id: userId!, companyId, hasCompanyId: !!companyId },
    skip: !userId,
  });

  useEffect((): void => {
    if (!data?.user_by_pk) {
      return;
    }

    const {
      id,
      email,
      first_name,
      last_name,
      mobile_phone,
      mobile_privacy_setting,
      landline_phone,
      landline_privacy_setting,
      email_privacy_setting,
      default_avatar,
      auth_provider_avatar_url,
      staff_user,
    } = data.user_by_pk;

    if (!data.user_by_pk.role_assignments.length) {
      // The ui doesnt currently handle users without roles assignments.
      // We logout to prevent locking the user on error page.
      Sentry.captureException('Login detected on an account without role assignments', { captureContext: { user: { email } } });
      logout();
    }

    const userAvailableContexts = getAvailableContexts(data);

    // sync user companies with segment.
    userAvailableContexts.forEach((context) => {
      window.analytics.group(context.companyId);
    });

    const verifiedContext = getContext(userAvailableContexts);
    const contactInformation: ContactInformation | undefined = email_privacy_setting
      ? {
        mobilePhone: mobile_phone,
        mobilePrivacySetting: mobile_privacy_setting,
        landlinePhone: landline_phone,
        landlinePrivacySetting: landline_privacy_setting,
        emailPrivacySetting: email_privacy_setting,
      }
      : undefined;

    if (verifiedContext.realm === Business_Profile_Enum.Buyer) {
      setCurrentBuyingCompanyId(verifiedContext.companyId);
    }

    setUser({
      id,
      contactInformation,
      sendbirdAccessToken: sendbirdAccessToken!,
      impersonateMode: !!impersonationToken,
      email: email!,
      first_name,
      last_name,
      avatars: {
        auth_provider_avatar_url,
        default_avatar,
      },
      isStaff: staff_user?.role === Staff_Role_Enum.Admin,
    });
    setCurrentContext(verifiedContext);
    setAvailableContexts(userAvailableContexts);
  }, [data?.user_by_pk, data?.company?.[0]]);
}
